import { useEffect, useMemo, useState, type FunctionComponent } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Button, Card, Col, Form, InputNumber, Modal, Row, Select, Space, Tag, Typography, message } from "antd";
import { Droplets, Footprints, Pencil, Scale } from "lucide-react";

import {
  asClientGoalConfig,
  ClientGoalType,
  ClientGoalUpdatedBy,
  reachedWeightThresholds,
  WEIGHT_GOAL_TIERS,
  WeightGoalDirection,
  weightGoalThresholds,
  type ClientGoalConfig,
  type WeightGoalTier,
} from "@fitness-app/data-models/entities/ClientGoal";

import { useTraineeLatestWeight } from "~/modules/Trainee/TraineeProfile/TraineeFeatures/TraineeMeasurements/useTraineeMeasurementsQueries";
import { supabase } from "~/store/initializeStore";
import { challengesHabitsKeys } from "./queryKeys";

// Defaults mirror the client apps' fallbacks (mobile health-sync.service / hydration-section):
// they apply whenever no explicit goal row exists, so an unset goal changes nothing for the client.
const DEFAULT_STEPS_GOAL = 10_000;
const DAILY_WATER_ML_PER_KG = 30;
const DEFAULT_WATER_GOAL_ML = 2500;

const tierLabels: Record<WeightGoalTier, string> = {
  bronze: "Brąz",
  silver: "Srebro",
  gold: "Złoto",
};

const tierColors: Record<WeightGoalTier, string> = {
  bronze: "volcano",
  silver: "default",
  gold: "gold",
};

type ClientGoalRow = {
  id: string;
  type: string;
  config: ClientGoalConfig;
  updatedBy: string;
  updatedAt: string;
};

type Props = {
  traineeId: string;
};

const useUpsertGoal = (traineeId: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ type, config }: { type: ClientGoalType; config: ClientGoalConfig }) => {
      const { error } = await supabase.from("client_goal").upsert(
        {
          traineeId,
          type,
          config,
          updatedBy: ClientGoalUpdatedBy.Trainer,
          updatedAt: new Date().toISOString(),
        } as never,
        { onConflict: "traineeId,type" },
      );

      if (error) {
        throw new Error(error.message);
      }
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: challengesHabitsKeys.goals(traineeId) });
      void queryClient.invalidateQueries({ queryKey: challengesHabitsKeys.badges(traineeId) });
      message.success("Cel zapisany");
    },
    onError: () => {
      message.error("Nie udało się zapisać celu");
    },
  });
};

const DailyGoalModal: FunctionComponent<{
  open: boolean;
  title: string;
  label: string;
  unit: string;
  step: number;
  min: number;
  max: number;
  initialValue: number;
  saving: boolean;
  onSave: (value: number) => void;
  onClose: () => void;
}> = ({ open, title, label, unit, step, min, max, initialValue, saving, onSave, onClose }) => {
  const [form] = Form.useForm<{ value: number }>();

  useEffect(() => {
    if (open) {
      form.setFieldsValue({ value: initialValue });
    }
  }, [open, initialValue, form]);

  return (
    <Modal
      open={open}
      title={title}
      okText="Zapisz"
      cancelText="Anuluj"
      confirmLoading={saving}
      onCancel={onClose}
      onOk={() => form.submit()}
      destroyOnClose
    >
      <Form form={form} layout="vertical" onFinish={({ value }) => onSave(value)}>
        <Form.Item name="value" label={label} rules={[{ required: true, message: "Podaj wartość celu" }]}>
          <InputNumber min={min} max={max} step={step} addonAfter={unit} style={{ width: "100%" }} />
        </Form.Item>
      </Form>
    </Modal>
  );
};

type WeightGoalFormValues = {
  direction: WeightGoalDirection;
  bronze: number;
  silver: number;
  gold: number;
};

const WeightGoalModal: FunctionComponent<{
  open: boolean;
  currentWeight: number | null;
  initialConfig: ClientGoalConfig | null;
  saving: boolean;
  onSave: (config: ClientGoalConfig) => void;
  onClose: () => void;
}> = ({ open, currentWeight, initialConfig, saving, onSave, onClose }) => {
  const [form] = Form.useForm<WeightGoalFormValues>();

  useEffect(() => {
    if (!open) {
      return;
    }

    const thresholds = initialConfig ? weightGoalThresholds(initialConfig) : [];
    const byTier = Object.fromEntries(thresholds.map((threshold) => [threshold.tier, threshold.value]));
    form.setFieldsValue({
      direction: initialConfig?.direction ?? WeightGoalDirection.Loss,
      bronze: byTier.bronze,
      silver: byTier.silver,
      gold: byTier.gold,
    });
  }, [open, initialConfig, form]);

  // Each tier must sit further along the goal direction (loss: descending, gain: ascending).
  // Bronze compares against the baseline (goal's startWeight, else the current measurement) —
  // otherwise a loss goal could start with a threshold already above the client's weight and the
  // badge would be awarded on save.
  const baseline = initialConfig?.startWeight ?? currentWeight ?? null;
  const monotonicRule = (tier: WeightGoalTier) => ({
    validator: async () => {
      const { direction, bronze, silver, gold } = form.getFieldsValue();
      const ordered = [bronze, silver, gold];
      const index = WEIGHT_GOAL_TIERS.indexOf(tier);
      const value = ordered[index];
      const previous = index === 0 ? baseline : ordered[index - 1];

      if (typeof value !== "number" || typeof previous !== "number") {
        return;
      }

      const valid = direction === WeightGoalDirection.Gain ? value > previous : value < previous;
      if (!valid) {
        if (index === 0) {
          throw new Error(
            direction === WeightGoalDirection.Gain
              ? `Pierwszy próg musi być powyżej wagi startowej (${previous} kg)`
              : `Pierwszy próg musi być poniżej wagi startowej (${previous} kg)`,
          );
        }
        throw new Error(
          direction === WeightGoalDirection.Gain
            ? "Kolejny próg musi być wyższy od poprzedniego"
            : "Kolejny próg musi być niższy od poprzedniego",
        );
      }
    },
  });

  return (
    <Modal
      open={open}
      title="Cel wagowy"
      okText="Zapisz"
      cancelText="Anuluj"
      confirmLoading={saving}
      onCancel={onClose}
      onOk={() => form.submit()}
      destroyOnClose
    >
      {typeof currentWeight === "number" ? (
        <Typography.Paragraph type="secondary">
          Aktualna waga klientki: <Typography.Text strong>{currentWeight} kg</Typography.Text>
        </Typography.Paragraph>
      ) : (
        <Typography.Paragraph type="secondary">Brak pomiarów wagi klientki.</Typography.Paragraph>
      )}
      <Form
        form={form}
        layout="vertical"
        onFinish={(values) =>
          onSave({
            direction: values.direction,
            thresholds: WEIGHT_GOAL_TIERS.map((tier, index) => ({
              tier,
              value: [values.bronze, values.silver, values.gold][index] as number,
            })),
            // Progress baseline for the first tier — captured once, kept across threshold edits.
            startWeight: initialConfig?.startWeight ?? currentWeight ?? undefined,
          })
        }
      >
        <Form.Item name="direction" label="Kierunek celu" rules={[{ required: true }]}>
          <Select
            options={[
              { value: WeightGoalDirection.Loss, label: "Redukcja (waga w dół)" },
              { value: WeightGoalDirection.Gain, label: "Masa (waga w górę)" },
            ]}
          />
        </Form.Item>
        {WEIGHT_GOAL_TIERS.map((tier) => (
          <Form.Item
            key={tier}
            name={tier}
            label={`Próg ${WEIGHT_GOAL_TIERS.indexOf(tier) + 1} — ${tierLabels[tier]}`}
            dependencies={["direction", ...WEIGHT_GOAL_TIERS.filter((other) => other !== tier)]}
            rules={[{ required: true, message: "Podaj wagę progu" }, monotonicRule(tier)]}
          >
            <InputNumber min={20} max={300} step={0.5} addonAfter="kg" style={{ width: "100%" }} />
          </Form.Item>
        ))}
      </Form>
    </Modal>
  );
};

const updatedByTag = (goal: ClientGoalRow | undefined) => {
  if (!goal) {
    return null;
  }

  return goal.updatedBy === ClientGoalUpdatedBy.Client ? (
    <Tag color="blue">ustawiony przez klientkę</Tag>
  ) : (
    <Tag>ustawiony przez trenera</Tag>
  );
};

const TraineeGoalsSection: FunctionComponent<Props> = ({ traineeId }) => {
  const [editedGoal, setEditedGoal] = useState<ClientGoalType | null>(null);
  const latestWeight = useTraineeLatestWeight();
  const upsertGoal = useUpsertGoal(traineeId);

  const goalsQuery = useQuery({
    queryKey: challengesHabitsKeys.goals(traineeId),
    refetchOnWindowFocus: true,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("client_goal")
        .select("id, type, config, updatedBy, updatedAt")
        .eq("traineeId", traineeId)
        .returns<ClientGoalRow[]>();

      if (error) {
        throw error;
      }

      return data ?? [];
    },
  });

  const goals = useMemo(() => {
    const byType = new Map((goalsQuery.data ?? []).map((goal) => [goal.type, goal]));
    return {
      water: byType.get(ClientGoalType.Water),
      steps: byType.get(ClientGoalType.Steps),
      weight: byType.get(ClientGoalType.Weight),
    };
  }, [goalsQuery.data]);

  const weightConfig = goals.weight ? asClientGoalConfig(goals.weight.config) : null;
  const weightThresholds = weightConfig ? weightGoalThresholds(weightConfig) : [];
  const reachedTiers = useMemo(() => {
    if (!weightConfig || typeof latestWeight.weight !== "number") {
      return new Set<WeightGoalTier>();
    }

    return new Set(reachedWeightThresholds(weightConfig, latestWeight.weight).map((threshold) => threshold.tier));
  }, [weightConfig, latestWeight.weight]);

  const defaultWaterGoal = latestWeight.weight
    ? Math.round((latestWeight.weight * DAILY_WATER_ML_PER_KG) / 100) * 100
    : DEFAULT_WATER_GOAL_ML;

  const waterTarget = goals.water ? asClientGoalConfig(goals.water.config).targetMl : undefined;
  const stepsTarget = goals.steps ? asClientGoalConfig(goals.steps.config).targetSteps : undefined;

  const saveGoal = (type: ClientGoalType) => (config: ClientGoalConfig) => {
    upsertGoal.mutate(
      { type, config },
      {
        onSuccess: () => {
          setEditedGoal(null);
        },
      },
    );
  };

  return (
    <Card
      title="Cele"
      extra={
        <Typography.Text type="secondary">
          Cele zasilają dashboard i statystyki klientki — nie wpływają na punktację wyzwań
        </Typography.Text>
      }
      loading={goalsQuery.isLoading}
    >
      {goalsQuery.isError ? (
        // A failed fetch must not render the defaults as if no goals existed — an edit from that
        // state would silently overwrite the persisted goal.
        <Typography.Text type="danger">
          Nie udało się pobrać celów — odśwież stronę, edycja jest zablokowana.
        </Typography.Text>
      ) : (
        <Row gutter={[16, 16]}>
          <Col xs={24} md={8}>
            <Card
              size="small"
              title={
                <Space size={8}>
                  <Droplets size={16} />
                  Woda
                </Space>
              }
              extra={
                <Button
                  size="small"
                  type="text"
                  aria-label="Edytuj cel nawodnienia"
                  icon={<Pencil size={14} />}
                  onClick={() => setEditedGoal(ClientGoalType.Water)}
                />
              }
            >
              <Space direction="vertical" size={4}>
                <Typography.Title level={4} style={{ margin: 0 }}>
                  {typeof waterTarget === "number" ? `${waterTarget} ml` : `${defaultWaterGoal} ml`}
                </Typography.Title>
                {typeof waterTarget === "number" ? (
                  updatedByTag(goals.water)
                ) : (
                  <Typography.Text type="secondary">domyślny ({DAILY_WATER_ML_PER_KG} ml × kg wagi)</Typography.Text>
                )}
              </Space>
            </Card>
          </Col>
          <Col xs={24} md={8}>
            <Card
              size="small"
              title={
                <Space size={8}>
                  <Footprints size={16} />
                  Kroki
                </Space>
              }
              extra={
                <Button
                  size="small"
                  type="text"
                  aria-label="Edytuj cel kroków"
                  icon={<Pencil size={14} />}
                  onClick={() => setEditedGoal(ClientGoalType.Steps)}
                />
              }
            >
              <Space direction="vertical" size={4}>
                <Typography.Title level={4} style={{ margin: 0 }}>
                  {(stepsTarget ?? DEFAULT_STEPS_GOAL).toLocaleString("pl-PL")}
                </Typography.Title>
                {typeof stepsTarget === "number" ? (
                  updatedByTag(goals.steps)
                ) : (
                  <Typography.Text type="secondary">domyślny</Typography.Text>
                )}
              </Space>
            </Card>
          </Col>
          <Col xs={24} md={8}>
            <Card
              size="small"
              title={
                <Space size={8}>
                  <Scale size={16} />
                  Waga
                </Space>
              }
              extra={
                <Button
                  size="small"
                  type="text"
                  aria-label="Edytuj cel wagowy"
                  icon={<Pencil size={14} />}
                  onClick={() => setEditedGoal(ClientGoalType.Weight)}
                />
              }
            >
              {weightThresholds.length ? (
                <Space direction="vertical" size={4}>
                  <Space size={4} wrap>
                    {weightThresholds.map((threshold) => (
                      <Tag
                        key={threshold.tier}
                        color={reachedTiers.has(threshold.tier) ? "green" : tierColors[threshold.tier]}
                      >
                        {tierLabels[threshold.tier]}: {threshold.value} kg
                        {reachedTiers.has(threshold.tier) ? " ✓" : ""}
                      </Tag>
                    ))}
                  </Space>
                  <Typography.Text type="secondary">
                    {weightConfig?.direction === WeightGoalDirection.Gain ? "cel: masa" : "cel: redukcja"}
                    {typeof latestWeight.weight === "number" ? ` · aktualnie ${latestWeight.weight} kg` : ""}
                  </Typography.Text>
                </Space>
              ) : (
                <Typography.Text type="secondary">
                  Brak celu — ustaw 3 progi (brąz / srebro / złoto), każdy nagradzany odznaką
                </Typography.Text>
              )}
            </Card>
          </Col>
        </Row>
      )}

      <DailyGoalModal
        open={editedGoal === ClientGoalType.Water}
        title="Cel nawodnienia"
        label="Dzienny cel wody"
        unit="ml"
        step={100}
        min={500}
        max={8000}
        initialValue={waterTarget ?? defaultWaterGoal}
        saving={upsertGoal.isLoading}
        onSave={(value) => saveGoal(ClientGoalType.Water)({ targetMl: value })}
        onClose={() => setEditedGoal(null)}
      />
      <DailyGoalModal
        open={editedGoal === ClientGoalType.Steps}
        title="Cel kroków"
        label="Dzienny cel kroków"
        unit="kroków"
        step={500}
        min={1000}
        max={50_000}
        initialValue={stepsTarget ?? DEFAULT_STEPS_GOAL}
        saving={upsertGoal.isLoading}
        onSave={(value) => saveGoal(ClientGoalType.Steps)({ targetSteps: value })}
        onClose={() => setEditedGoal(null)}
      />
      <WeightGoalModal
        open={editedGoal === ClientGoalType.Weight}
        currentWeight={latestWeight.weight ?? null}
        initialConfig={weightConfig}
        saving={upsertGoal.isLoading}
        onSave={saveGoal(ClientGoalType.Weight)}
        onClose={() => setEditedGoal(null)}
      />
    </Card>
  );
};

export default TraineeGoalsSection;
